import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/neptune/Navbar";
import Footer from "../components/neptune/Footer";
import { useLeadModal } from "../components/neptune/LeadModalContext";
import { useContent } from "../context/ContentContext";

const fallbackBeliefs = [
  { text: "Payments are infrastructure, not theater." },
  { text: "Approval rates aren't a roulette." },
  { text: "Routing should be invisible." },
  { text: "A 12-week settlement is a bug." },
  { text: "Modern shouldn't be a feature — it should be the floor." },
];

// Cinematic block-level reveal (blur + y) — used for chrome-text headings

function CinematicH1({ text, className }) {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 32, filter: "blur(14px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {text}
    </motion.h1>
  );
}

function CinematicH2({ text, className, inView = false }) {
  const props = inView
    ? {
        initial: { opacity: 0, y: 32, filter: "blur(14px)" },
        whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
        viewport: { once: true, amount: 0.1 },
      }
    : {
        initial: { opacity: 0, y: 32, filter: "blur(14px)" },
        animate: { opacity: 1, y: 0, filter: "blur(0px)" },
      };
  return (
    <motion.h2
      {...props}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {text}
    </motion.h2>
  );
}

export default function About() {
  const { open: openLead } = useLeadModal();
  const { t } = useContent();
  const beliefs = t("about.beliefs", fallbackBeliefs);

  // Parallax for hero
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, -120]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0.4]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 0.96]);

  // Parallax for orbs
  const orb1Y = useTransform(scrollY, [0, 1500], [0, -200]);
  const orb2Y = useTransform(scrollY, [0, 1500], [0, 250]);
  const orb3Y = useTransform(scrollY, [0, 2500], [0, -400]);

  // Why section parallax
  const whyRef = useRef(null);
  const { scrollYProgress: whyProgress } = useScroll({
    target: whyRef,
    offset: ["start end", "end start"],
  });
  const whyTitleY = useTransform(whyProgress, [0, 1], [80, -80]);
  const whyIconRotate = useTransform(whyProgress, [0, 1], [-30, 30]);
  const whyIconScale = useSpring(
    useTransform(whyProgress, [0, 0.5, 1], [0.8, 1.15, 0.8]),
    { stiffness: 100, damping: 20 }
  );

  // Beliefs section progress for vertical glow line
  const beliefsRef = useRef(null);
  const { scrollYProgress: beliefsProgress } = useScroll({
    target: beliefsRef,
    offset: ["start 80%", "end 30%"],
  });
  const lineHeight = useTransform(beliefsProgress, [0, 1], ["0%", "100%"]);

  return (
    <main
      data-testid="about-page"
      className="relative min-h-screen bg-[#0a0a0a] text-white font-body overflow-x-hidden"
    >
      <Navbar />

      {/* Drifting ambient orbs */}
      <motion.div
        style={{ y: orb1Y }}
        className="pointer-events-none fixed top-[10%] left-[-15%] h-[500px] w-[500px] rounded-full bg-[#0055FF]/20 blur-[140px]"
      />
      <motion.div
        style={{ y: orb2Y }}
        className="pointer-events-none fixed top-[40%] right-[-10%] h-[420px] w-[420px] rounded-full bg-[#00E5FF]/12 blur-[140px]"
      />
      <motion.div
        style={{ y: orb3Y }}
        className="pointer-events-none fixed top-[70%] left-[30%] h-[500px] w-[500px] rounded-full bg-[#0055FF]/15 blur-[160px]"
      />

      {/* Hero */}
      <section
        ref={heroRef}
        data-testid="about-hero"
        className="relative pt-40 sm:pt-52 pb-24 sm:pb-36 ocean-glow"
      >
        <div className="absolute inset-0 subtle-grid pointer-events-none opacity-80" />
        <motion.div
          style={{ y: heroY, opacity: heroOpacity, scale: heroScale }}
          className="relative max-w-7xl mx-auto px-6 sm:px-8"
        >
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500"
          >
            <span className="h-px w-6 bg-zinc-700" />
            {t("about.hero.eyebrow", "About")}
          </motion.div>

          <CinematicH1
            text={t("about.hero.title", "Designed for the future of payments. Built to be timeless.")}
            className="font-display mt-6 text-5xl sm:text-7xl lg:text-[92px] lg:leading-[1.0] font-semibold tracking-tighter chrome-text max-w-5xl"
          />

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 text-[16px] sm:text-[19px] text-zinc-400 leading-relaxed max-w-2xl"
          >
            {t("about.hero.sub", "A manifesto, in a few short lines.")}
          </motion.p>

          {/* Animated scroll indicator */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-20 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-zinc-600"
          >
            <span>Scroll</span>
            <motion.div
              className="h-px w-12 bg-gradient-to-r from-zinc-700 to-transparent"
              animate={{ scaleX: [1, 1.4, 1], opacity: [0.4, 1, 0.4] }}
              style={{ transformOrigin: "left" }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Why we built Neptune — parallax */}
      <section
        ref={whyRef}
        data-testid="about-why"
        className="relative py-32 sm:py-44 border-t border-white/[0.06]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 gap-x-12">
            <div className="lg:col-span-4">
              <motion.div
                style={{ rotate: whyIconRotate, scale: whyIconScale }}
                className="inline-flex items-center justify-center h-16 w-16 rounded-2xl border border-white/[0.08] bg-white/[0.025] relative overflow-hidden"
              >
                <motion.div
                  className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(0,229,255,0.45),transparent_70%)]"
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 3.5, repeat: Infinity }}
                />
                <motion.div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "conic-gradient(from 0deg, rgba(0,229,255,0) 0deg, rgba(0,229,255,0.6) 80deg, rgba(0,85,255,0) 180deg)",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMask:
                      "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    padding: "1px",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
                <Sparkles
                  className="relative h-6 w-6 text-[#00E5FF]"
                  strokeWidth={1.5}
                />
              </motion.div>
              <motion.div
                style={{ y: whyTitleY }}
                className="mt-7 text-[11px] uppercase tracking-[0.22em] text-zinc-500"
              >
                {t("about.why.eyebrow", "Why we built Neptune")}
              </motion.div>
            </div>

            <motion.div style={{ y: whyTitleY }} className="lg:col-span-8">
              <CinematicH2
                text={t("about.why.title_part1", "Most payment infrastructure was built before the iPhone existed.")}
                inView
                className="font-display text-3xl sm:text-5xl lg:text-[64px] lg:leading-[1.05] font-semibold tracking-tighter chrome-text"
              />
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="font-display mt-3 text-3xl sm:text-5xl lg:text-[64px] lg:leading-[1.05] font-semibold tracking-tighter text-white/55"
              >
                {t("about.why.title_part2", "We don't accept that.")}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-10 text-[16px] sm:text-[19px] text-zinc-400 leading-relaxed max-w-2xl"
              >
                {t("about.why.body1", "Neptune is what happens when a small team of payments and infrastructure engineers decides to start over — and build the rails the way they should have always worked.")}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-5 text-[15px] sm:text-[17px] text-zinc-500 leading-relaxed max-w-2xl whitespace-pre-line"
              >
                {t("about.why.body2", "No legacy. No theater. No black box.\nJust intelligent infrastructure for the businesses that will define the next decade of commerce.")}
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Beliefs / Manifesto — cinematic with progress line */}
      <section
        ref={beliefsRef}
        data-testid="about-beliefs"
        className="relative py-32 sm:py-44 border-t border-white/[0.06] overflow-hidden"
      >
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[1000px] rounded-full bg-[#0055FF]/12 blur-[140px]" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-[11px] uppercase tracking-[0.22em] text-zinc-500"
          >
            — What we believe
          </motion.div>

          <div className="mt-16 sm:mt-20 relative max-w-4xl">
            {/* Animated progress line */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-white/[0.06]">
              <motion.div
                style={{ height: lineHeight }}
                className="w-full bg-gradient-to-b from-[#00E5FF] via-[#0055FF] to-transparent shadow-[0_0_12px_rgba(0,229,255,0.5)]"
              />
            </div>

            <div className="space-y-7 sm:space-y-9 pl-7 sm:pl-12">
              {beliefs.map((b, i) => (
                <BeliefLine key={i} index={i} text={typeof b === "string" ? b : b.text} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — cinematic zoom + glow pulse */}
      <section
        id="contact"
        data-testid="about-contact"
        className="relative py-32 sm:py-48 border-t border-white/[0.06] overflow-hidden"
      >
        {/* Pulsing background */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-0"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[140%] bg-[radial-gradient(ellipse_50%_60%_at_50%_50%,_rgba(0,85,255,0.35),_transparent_70%)]" />
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[60%] bg-[radial-gradient(ellipse_30%_50%_at_50%_50%,_rgba(0,229,255,0.25),_transparent_80%)]" />
        </motion.div>
        <div className="absolute inset-0 subtle-grid opacity-50" />

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <CinematicH2
            text={`${t("about.cta.headline_part1", "If you feel the same —")} ${t("about.cta.headline_part2", "let's build.")}`}
            inView
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter chrome-text leading-[1.05]"
          />
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-7 text-zinc-400 text-[16px] sm:text-[18px] max-w-xl mx-auto leading-relaxed"
          >
              {t("about.cta.body", "We work with a small, deliberate group of operators. If that's you, get in touch.")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center"
          >
            <button
              type="button"
              onClick={() => openLead("contact")}
              data-testid="about-get-in-touch-btn"
              className="group relative inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-all px-6 py-3.5 text-[14px] font-medium overflow-hidden"
            >
              <span className="relative z-10 inline-flex items-center gap-1.5">
                {t("about.cta.button", "Get in touch")}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
            <Link
              to="/contact"
              data-testid="about-contact-link"
              className="inline-flex items-center justify-center rounded-full glass hover:bg-white/[0.06] text-white transition-colors px-6 py-3.5 text-[14px] font-medium"
            >
              Visit contact page
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function BeliefLine({ index, text }) {
  return (
    <motion.div
      data-testid={`belief-${index}`}
      initial={{ opacity: 0, x: -30, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-15% 0px -15% 0px" }}
      transition={{
        duration: 0.9,
        delay: index * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group relative"
    >
      {/* Marker dot */}
      <motion.div
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 + index * 0.05, type: "spring", stiffness: 220 }}
        className="absolute -left-7 sm:-left-12 top-3 h-2 w-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#0055FF] shadow-[0_0_12px_rgba(0,229,255,0.7)]"
      />
      {/* Index */}
      <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600 mb-1.5">
        {String(index + 1).padStart(2, "0")}
      </div>
      <h3 className="font-display text-2xl sm:text-3xl lg:text-[36px] lg:leading-[1.25] font-medium tracking-tight text-white group-hover:text-[#e8f4ff] transition-colors duration-500">
        {text}
      </h3>
    </motion.div>
  );
}
